import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { useIntl } from '@atlassian/jira-intl';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import type { State } from '../../../../common/types/index.tsx';
import type { ResultUseMatches } from '../../../../common/types/use-matches/index.tsx';
import { COMPAL_MENU_SCREEN_READER_ID } from '../../../../common/ui/command-palette-search-dropdown/constants.tsx';
import { useCommandPalette } from '../../../../controllers/command-palette/index.tsx';
import messages from './messages.tsx';

const CommandPaletteScreenReaderContent: typeof ScreenReaderOnly = ({ ...props }) => (
	<ScreenReaderOnly
		aria-live="polite"
		aria-atomic="false"
		tabIndex={-1}
		{...props}
		id={COMPAL_MENU_SCREEN_READER_ID}
	/>
);

export const ScreenReaderContent = ({
	activeCommand,
	results,
}: Pick<ResultUseMatches, 'activeCommand' | 'results'>) => {
	const [{ search, activeIndex }, { forceFocusSearch }] = useCommandPalette();
	const previousSearch = usePrevious(search);

	const { getScreenReaderText } = useGetScreenReaderText();
	const [screenReaderText, setScreenReaderText] = useState<string | null>(null);

	const debouncedSetScreenReaderText = useMemo(() => debounce(setScreenReaderText, 400), []);

	useEffect(
		() => () => {
			debouncedSetScreenReaderText?.cancel?.();
		},
		[debouncedSetScreenReaderText],
	);

	useEffect(() => {
		if (previousSearch !== search) {
			debouncedSetScreenReaderText(getScreenReaderText({ activeCommand, results, activeIndex }));
		}
	}, [
		activeCommand,
		getScreenReaderText,
		previousSearch,
		results,
		search,
		activeIndex,
		debouncedSetScreenReaderText,
	]);

	const onFocus = useCallback(() => {
		forceFocusSearch(true);
	}, [forceFocusSearch]);

	return (
		<CommandPaletteScreenReaderContent
			data-testid="command-palette.ui.modal.modal-content.screen-reader-content.command-palette-screen-reader-content"
			onFocus={onFocus}
		>
			{screenReaderText}
		</CommandPaletteScreenReaderContent>
	);
};

export const useGetScreenReaderText = () => {
	const { formatMessage } = useIntl();
	const getScreenReaderText = ({
		activeCommand,
		results,
		activeIndex,
	}: Pick<ResultUseMatches, 'activeCommand' | 'results'> & Pick<State, 'activeIndex'>) => {
		if (!activeCommand) {
			return null;
		}
		const selectedOptionLabel = activeCommand?.name;
		const selectedIndex = activeIndex + 1;
		const totalSuggestions = results.filter((result) => !result.isGroup).length;

		return formatMessage(messages.selectedOption, {
			selectedOptionLabel,
			selectedIndex,
			totalSuggestions,
		});
	};
	return { getScreenReaderText };
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ScreenReaderOnly = styled.div({
	position: 'absolute',
	width: '1px',
	height: '1px',
	padding: 0,
	overflow: 'hidden',
	clip: 'rect(0, 0, 0, 0)',
	wordWrap: 'normal',
	border: 0,
});
